import React, {useEffect, useState} from 'react';
import {get} from 'lodash';
import propTypes from 'prop-types';
import styled from '@emotion/styled';
import parse from 'html-react-parser';
import { Select, MenuItem, InputLabel, FormControl, ButtonBase } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {theme} from '../../../themes/default-theme';
import {ReactSVG} from 'react-svg';
import chevronDown from '../../../assets/icons/chevron-down.svg';
import mobileCheck from '../../../util/mobileCheck';
import Router from 'next/router';

export const dropdownTheme = createTheme({
  ...theme,
});

const liteTheme = createTheme({
  ...theme,
});

dropdownTheme.components = {
  ...theme.components,
  Complete: {
    styleOverrides: {
      'fieldset': {
        background: 'red'
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        minWidth: 70,
        borderRadius: 0,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
        border: `1px solid ${theme.palette.middleGrey}`,
        color: theme.palette.white,
        borderLeft: 0,
      },
      notchedOutline: {
        border: 0,
      }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        "&.MuiInputLabel-outlined": {
          transform: 'translate(0, 22px)',
          paddingLeft: 20,
          paddingRight: 0,
          position: 'relative',
          marginTop: '-15px',
          color: theme.palette.gunmentalGrey,
          fontSize: 12,
          lineHeight: '15px',
          '&.MuiInputLabel-shrink': {
            transform: 'translateY(-50%)'
          },
          [theme.breakpoints.up('md')]: {
            fontSize: 16,
            marginTop: 0
          }
        }
      },
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        "&.Mui-focused": {
          color: theme.palette.darkGrey,
        },
        '&.MuiFormLabel-filled': {
          display: 'none'
        }
      }
    }
  },
  MuiList: {
    styleOverrides: {
      root: {
        "&.MuiList-padding": {
          padding: 0,
        }
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        "&.MuiPaper-elevation8": {
          boxShadow: 'none',
          overflow: 'visible',
          marginTop: 20,
          backgroundColor: 'white',
          border: `1px solid ${theme.palette.middleGrey}`,
        }
      }
    }
  },
  MuiSelect: {
    styleOverrides: {
      root: {
        maxHeight: '30px',
        lineHeight: '30px',
        "&.MuiSelect-select:focus": {
          backgroundColor: 'transparent'
        },
        [theme.breakpoints.up('md')]: {
          padding: '14.5px 0px',
        },
        [theme.breakpoints.down('sm')]: {
          height: '30px !important',
          padding: 0
        }
      },
    }
  },
  MuiListItem: {
    styleOverrides: {
      root: {
        fontSize: '16px',
        "&.Mui-selected, &.Mui-selected:hover": {
          backgroundColor: theme.palette.vividBlue,
          color: 'white'
        }
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '16px',
        overflow: 'visible',
        padding: '12.5px 12.5px',
        textTransform: 'capitalize',
        "&:not(:last-child)": {
          borderBottom: `1px solid ${theme.palette.middleGrey}`,
        },
        "&.Mui-disabled": {
          opacity: 0.5
        }
      }
    }
  }
};

liteTheme.components = {
  ...dropdownTheme.components,
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        minWidth: 75,
        borderRadius: 0,
        borderTopRightRadius: 8,
        borderBottomRightRadius: 8,
        border: `1px solid #575757`,
        borderLeft: 0,
        color: theme.palette.white,
        background: '#292929',
        
      },
      notchedOutline: {
        border: 0,
      }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      root: {
        "&.MuiInputLabel-outlined": {
          color: theme.palette.white,
          fontSize: 12,
          lineHeight: '9px',
          transform: 'translate(0, 18px)',
          paddingLeft: 20,
          paddingRight: 0,
          position: 'relative',
          marginTop: '-9px',
          textAlign: 'center',
          paddingLeft: 0,
          [theme.breakpoints.up('md')]: {
            fontSize: 16,
            marginTop: 0
          }
        }
      },
    }
  },
  MuiFormLabel: {
    styleOverrides: {
      root: {
        "&.Mui-focused": {
          color: theme.palette.white,
        },
        '&.MuiFormLabel-filled': {
          display: 'none'
        }
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        "&.MuiPaper-elevation8": {
          boxShadow: 'none',
          marginTop: 20,
          backgroundColor: 'white',
          border: `1px solid #575757`,
        }
      }
    }
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontSize: '16px',
        padding: '12.5px 12.5px',
        background: '#292929 !important',
        color: 'white',
        "&:hover, &.Mui-active, &.Mui-focused": {
          background: '#292929!important',
        },
        "&:not(:last-child)": {
          borderBottom: `1px solid #575757`
        }
      }
    }
  }
};

const ChevronIcon = styled(ButtonBase)`
  pointer-events: none;
  width: 24px;
  height: 24px;
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-60%);
  transition: transform 300ms ease-in-out;
  margin-top: 1px;

  ${theme.breakpoints.up('md')} {
    top: 10px;
  }

  svg {
    [stroke-width] {
      stroke: ${theme.palette.gunmentalGrey};
    }
  }

  ${props => props.background == 'dark' && `
    svg {
      [stroke-width] {
        stroke: ${theme.palette.white};
      }
    }
  `}
`

const BadgeItem = styled.div`
  position: absolute;
  background: #00AEEF;
  color: #fff;
  line-height: 14px;
  top: -7px;
  padding: 1px;
  border-radius: 4px;
  font-size: 7px;
  text-transform: uppercase;
  font-weight: 700;
  min-width: 24px;
  text-align: center;
}
`

const dropdownDefaultPropOnChange = () => {};
const dropdownDefaultPropOnUpdate = () => {};

const Dropdown = ({
  label = 'Select',
  options,
  onChange = dropdownDefaultPropOnChange,
  onUpdate = dropdownDefaultPropOnUpdate,
  alwaysNative = false,
  neverNative = false,
  defaultValue = undefined,
  value = undefined,
  formVal = undefined,
  error = false,
  showComplete = false,
  disabled = false,
  link = false,
  background = null,
  open = false,
  active = false,
  closeOnScroll = false
}) => {

  const [isNative, setNative] = useState(true);
  const [isOpen, setOpen] = useState(open);

  useEffect(() => {
    setOpen(open);
  }, [open])

  const handleChange = (value) => {
    if (link) {
      Router.push(value);
      return;
    }
    const item = options.find(item => item.value.toString() === value.toString());
    onChange(item)
    onUpdate(item) // for react hook forms
  }

  useEffect(() => {
    if (neverNative) {
      setNative(false);
    } else {
      const native = alwaysNative ? true : mobileCheck();
      setNative(native)
    }
  },[])


  useEffect(() => {
    if (!closeOnScroll || !isOpen) {
      return;
    }
    const handleScroll = () => setOpen(false);
    window.addEventListener("wheel", handleScroll, {passive: true});
    window.addEventListener("touchmove", handleScroll, {passive: true});
    return () => {
      window.removeEventListener("wheel", handleScroll);
      window.removeEventListener("touchmove", handleScroll);
    };
  }, [closeOnScroll, isOpen])

  const val = typeof(value) === 'object' ? get(value, 'value', undefined) : value;

  return (
    <ThemeProvider theme={background === 'dark' ? liteTheme : dropdownTheme}>
      <FormControl variant="outlined" classes={{ root: showComplete && !error ? dropdownTheme.Complete : ''}}>
        {!isNative && !formVal &&
        <InputLabel 
          disableAnimation={true}
          focused={false}
          shrink={false}
          disabled={disabled}
          >{label}</InputLabel>
        }
        <Select
          onChange={(e) => {handleChange(e.target.value)}}
          native={isNative}
          value={formVal ? formVal.value : val || ''}
          defaultValue={defaultValue || undefined}
          error={error}
          disabled={disabled}
          {...(closeOnScroll ? {open: isOpen} : {})}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          IconComponent={() => (
            <ChevronIcon background={background}>
              <ReactSVG src={chevronDown} />
            </ChevronIcon>
          )}
          MenuProps={{
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "center"
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "center"
            },
            getContentAnchorEl: null
          }}
        >
          {isNative && !defaultValue && label && <option value="">{label}</option> }
          {options.map(item => (
            isNative ? 
              <option key={item.id || item.value} value={item.value}>{parse(item.text)}</option>
              :
              <MenuItem key={item.id || item.value} value={item.value}>
                  {
                    item.badge && <BadgeItem>{item.badge}</BadgeItem>
                  }
                  {parse(item.text)}
              </MenuItem>
          ))}
        </Select>
      </FormControl>
    </ThemeProvider>
  )
}

Dropdown.propTypes = {
  label: propTypes.string,
  options: propTypes.array.isRequired,
  onChange: propTypes.func,
  onUpdate: propTypes.func,
  alwaysNative: propTypes.bool,
  formVal: propTypes.object,
  defaultValue: propTypes.string,
  value: propTypes.any,
  error: propTypes.bool,
  showComplete: propTypes.bool,
  disabled: propTypes.bool,
  link:  propTypes.bool,
  neverNative: propTypes.bool,
  open: propTypes.bool,
  background: propTypes.string,
  active: propTypes.bool,
  closeOnScroll: propTypes.bool
}

export default Dropdown
